import type { Locale, PlanDay } from '@itin/shared'
import { locateInTrip, zoneCityLabel } from '@itin/shared'
import { useEffect, useState } from 'react'
import { KIND_GLYPH } from './ItemForm.js'
import { useLocale, useT } from '../lib/i18n.js'

/**
 * Where you are in the trip, and what is next.
 *
 * A nine-day itinerary is one long page, and a traveller standing in Paris on
 * Thursday afternoon does not want nine days — they want the next two things.
 * Today they get the whole document and have to find their place in it, every
 * time they open the link.
 *
 * This is only correct because times are now correct. "What is happening now"
 * requires knowing what time it is WHERE THEY ARE, which is the trip's zone and
 * not the browser's — a phone that has not caught up after a flight, or a
 * relative reading along from São Paulo, would otherwise be shown the wrong day.
 * Products that store times as strings cannot build this at all.
 */

/** Ticks once a minute, so "next" stops being stale on a page left open. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])
  return now
}

function formatTime(iso: string, timeZone: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(new Date(iso))
}

export function TripProgress({
  days,
  timeZone,
  onJumpToDay,
}: {
  days: PlanDay[]
  timeZone: string
  onJumpToDay(index: number): void
}) {
  const t = useT()
  const { locale } = useLocale()
  const now = useNow()
  const position = locateInTrip(days, timeZone, now)

  // Nothing to say once it is over. A finished trip should read as a keepsake,
  // not as a dashboard reporting that it has finished.
  if (position.phase === 'after') return null

  const next = position.next
  const nextZone = next?.timezone ?? timeZone

  return (
    <aside
      data-testid="trip-progress"
      className="no-print flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gold/30 bg-[#fdfaf3] px-4 py-3"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="label-caps text-slate-500">
          {position.phase === 'before'
            ? position.daysUntil === 0
              ? t('progress.startsToday')
              : t('progress.startsIn', { days: String(position.daysUntil) })
            : position.dayIndex === null
              ? t('progress.underway')
              : t('progress.dayOf', {
                  day: String(position.dayIndex + 1),
                  total: String(days.length),
                })}
        </span>

        {next ? (
          <span className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="text-slate-500">{t('progress.next')}</span>
            <span className="font-medium tabular-nums text-slate-900">
              {next.startsAt ? formatTime(next.startsAt, nextZone, locale) : ''}
            </span>
            <span aria-hidden="true">{KIND_GLYPH[next.kind]}</span>
            <span className="min-w-0 truncate text-slate-800">{next.title}</span>
            {nextZone !== timeZone && (
              <span className="text-xs text-slate-500">
                ({zoneCityLabel(nextZone, locale)})
              </span>
            )}
          </span>
        ) : (
          <span className="text-sm text-slate-500">{t('progress.nothingScheduled')}</span>
        )}
      </div>

      {position.dayIndex !== null && (
        <button
          type="button"
          onClick={() => onJumpToDay(position.dayIndex!)}
          className="flex min-h-11 shrink-0 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 transition-colors hover:border-gold hover:bg-[#fdfaf3] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:min-h-10"
        >
          {t('progress.jumpToToday')}
        </button>
      )}
    </aside>
  )
}
